import { formatDistance, startOfDay, subDays } from 'date-fns'
import { observer } from 'mobx-react'
import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Container } from '../../components/Animations'
import { PrimaryButton, SecondaryButton } from '../../components/Button'
import {
  EmptyState,
  InlineNotice,
  InlinePill,
  Surface
} from '../../components/DashboardPrimitives'
import LoadingBar from '../../components/LoadingBar'
import VerificationBanner from '../../components/VerificationBanner'
import { Chevron } from '../../icons/Chevron'
import { ManagedPaymentEvent, RadomEvent, parseManagedEvent } from '../../state/Events'
import Radom, {
  Deposit,
  DepositAccount,
  DepositAccountType,
  IConversionRate,
  ManagedEventWithRelatedCustomer,
  Payout,
  RefundEntity,
  Swap,
  Transaction,
  TransactionKind,
  TransactionStatus,
  WithdrawalRequest
} from '../../state/Radom'
import Dropdown, { DropdownContext, DropdownItem } from '../../components/Dropdown'
import { Currencies } from '../../util/Currencies'
import ConvertController from '../../state/ConvertController'
import Treasury from '../../state/Treasury'
import User from '../../state/User'
import {
  ManagedPaymentMethod,
  getMethod,
  getMethodByTicker,
  getMethodLogoByTicker,
  getPaymentMethods,
  prodNetworks,
  testNetworks
} from '../../util/Managed'
import { payoutStatusLabel } from '../../util/PayoutStatus'
import { TokenCurrencies, errorToast, formatAssetAmount, successToast, useTestMode } from '../../util/Util'
import { usePermissions } from '../../util/permissions'
import MoneySparkline, { SparklinePoint } from './MoneySparkline'
import {
  AccountRowButton,
  AssetLogoCircle,
  Eyebrow,
  EyebrowRow,
  FeedAmount,
  FeedBadge,
  FeedLink,
  FeedList,
  FeedSub,
  FeedTitle,
  GroupLabel,
  GroupStack,
  GroupSurface,
  HeroActions,
  HeroBlock,
  HeroMeta,
  HeroRow,
  HeroTotal,
  HistoryTextButton,
  InlineTextButton,
  InlineTextLink,
  MoneyColumn,
  MoneyLayout,
  MoneyWorkspace,
  MutedNote,
  PulseGrid,
  PulseLabel,
  PulseStat,
  PulseValue,
  RowAmount,
  RowAmountSub,
  RowChevron,
  RowEnd,
  RowEndInline,
  RowIconCircle,
  RowMain,
  RowSub,
  RowTitle,
  RowTitleLine,
  SideColumn,
  CurrencySymbolGlyph,
  CurrencySwitchTrigger
} from './MoneyStyles'

/** One managed balance normalized for this screen (mirrors Funds/Overview). */
interface BalanceRow {
  key: string
  method: ManagedPaymentMethod
  ticker: string
  isFiat: boolean
  isTestnet: boolean
  balance: number
  fiatValue: number
  hasQuote: boolean
  networkLabel: string
}

/** All networks holding the same ticker, presented as a single asset row. */
interface AssetGroup {
  ticker: string
  name: string
  logo?: string
  totalBalance: number
  totalFiatValue: number
  hasQuote: boolean
  rows: BalanceRow[]
}

export type FeedDirection = 'in' | 'out'

interface FeedRow {
  id: string
  label: string
  amountLabel: string
  direction: FeedDirection
  statusLabel?: string
  tone?: 'green' | 'amber' | 'red' | 'blue' | 'neutral'
  timestamp: number
  href: string
}

/** One transaction from the shared listing, reduced to a drill-down line. */
export interface AssetActivityRow {
  id: string
  label: string
  amountLabel: string
  direction: FeedDirection
  timestamp: number
}

// Same default kind/status sets the Transactions view queries with.
export const ALL_TRANSACTION_KINDS = [
  TransactionKind.Deposit,
  TransactionKind.Payment,
  TransactionKind.Payout,
  TransactionKind.Withdrawal,
  TransactionKind.Refund,
  TransactionKind.Swap
]

export const ALL_TRANSACTION_STATUSES = [
  TransactionStatus.Successful,
  TransactionStatus.Pending,
  TransactionStatus.Processing,
  TransactionStatus.InReview,
  TransactionStatus.Incomplete,
  TransactionStatus.Failed
]

const COMPLETED_PAYMENT_EVENT_NAMES = ['PaymentEvent', 'RecurringPaymentEvent', 'SubscriptionPaymentEvent']

/**
 * Unified-treasury balance rows carry a bare asset code and no network. Rows
 * whose code is one of these ISO currencies belong to the cash section.
 */
const FIAT_ASSET_CODES = ['EUR', 'USD', 'GBP']

/** Same definition of a completed payment as the Home dashboard metrics. */
const isCompletedPaymentEvent = (event: RadomEvent): event is ManagedPaymentEvent =>
  COMPLETED_PAYMENT_EVENT_NAMES.includes(event.name)

/** Payments settled over the open-banking cash rail read as bank credits. */
const isOpenBankingPayment = (event: ManagedPaymentEvent): boolean =>
  !!event.openBankingPaymentData || event.paymentMethod?.name === 'Fiat'

const withdrawalStatusTone = (status: string): FeedRow['tone'] =>
  status === 'complete'
    ? 'green'
    : status === 'failed'
      ? 'red'
      : status === 'processing' ? 'blue' : 'amber'

/** "pendingApproval" → "Pending approval" for the activity feed. */
const withdrawalStatusLabel = (status: string): string => {
  if (status === 'awaitingInventory') {
    // Unified-treasury wait state; render white-label copy, not the backend name.
    return 'Awaiting funds'
  }
  const spaced = status.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

const payoutStatusTone = (status: Payout['status']): FeedRow['tone'] => {
  switch (status) {
  case 'Complete':
    return 'green'
  case 'Failed':
  case 'Expired':
    return 'red'
  case 'Processing':
  case 'Pending':
    return 'blue'
  default:
    return 'amber'
  }
}

/**
 * Bank-statement money formatting: always two decimals, currency symbol for
 * ISO currencies, ticker suffix for token default currencies (USDC etc).
 */
export const formatMoney = (value: number, currency: string): string => {
  const plainFormat = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  if (TokenCurrencies.includes(currency)) {
    return `${plainFormat.format(value)} ${currency}`
  }

  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value)
  } catch {
    return `${plainFormat.format(value)} ${currency}`
  }
}

/**
 * Reduces one row of the shared transactions listing to a drill-down line for
 * the given asset, or null when the transaction does not touch that asset.
 * Network/token pairs resolve through getMethod so ticker matching works the
 * same way the Transactions view labels its rows.
 */
export const activityRowForAsset = (transaction: Transaction, ticker: string): AssetActivityRow | null => {
  const fallbackTime = new Date(transaction.createdAt).getTime()

  switch (transaction.kind) {
  case TransactionKind.Withdrawal: {
    const withdrawal = transaction.payload as WithdrawalRequest
    if (getMethod(withdrawal.network, withdrawal.token).ticker !== ticker) {
      return null
    }
    return {
      id: `withdrawal-${withdrawal.id}`,
      label: 'Withdrawal',
      amountLabel: `-${formatAssetAmount(withdrawal.withdrawalAmount, ticker)} ${ticker}`,
      direction: 'out',
      timestamp: new Date(withdrawal.requestedAt).getTime() || fallbackTime
    }
  }
  case TransactionKind.Payment: {
    const payment = transaction.payload as ManagedEventWithRelatedCustomer
    const parsed = parseManagedEvent(payment)
    if (!parsed || !('paymentMethod' in parsed)) {
      return null
    }
    const timestamp = parsed.date.getTime() || fallbackTime
    if (parsed.paymentMethod.ticker === ticker) {
      return {
        id: `payment-${payment.id}`,
        label: parsed.label,
        amountLabel: `+${formatAssetAmount(parsed.amount, ticker)} ${ticker}`,
        direction: 'in',
        timestamp
      }
    }
    // Bank-rail payments credit the balance in the on-ramp asset (USDC).
    const onrampGrossAmount = payment.amountsBreakdown?.onrampGrossAmount
    const onrampTicker = getMethod(
      payment.amountsBreakdown?.onrampNetwork ?? 'Ethereum',
      payment.amountsBreakdown?.onrampToken
    ).ticker
    if (onrampGrossAmount && onrampTicker === ticker) {
      return {
        id: `payment-${payment.id}`,
        label: parsed.label,
        amountLabel: `+${formatAssetAmount(onrampGrossAmount, ticker)} ${ticker}`,
        direction: 'in',
        timestamp
      }
    }
    return null
  }
  case TransactionKind.Refund: {
    const refund = transaction.payload as RefundEntity
    if (getMethod(refund.network, refund.token).ticker !== ticker) {
      return null
    }
    return {
      id: `refund-${refund.id}`,
      label: 'Refund',
      amountLabel: `-${formatAssetAmount(refund.amount, ticker)} ${ticker}`,
      direction: 'out',
      timestamp: new Date(refund.createdAt).getTime() || fallbackTime
    }
  }
  case TransactionKind.Swap: {
    const swap = transaction.payload as Swap
    const timestamp = new Date(swap.createdAt).getTime() || fallbackTime
    if (getMethod(swap.fromNetwork, swap.fromToken).ticker === ticker) {
      return {
        id: `swap-${swap.id}`,
        label: 'Convert',
        amountLabel: `-${formatAssetAmount(swap.fromAmount, ticker)} ${ticker}`,
        direction: 'out',
        timestamp
      }
    }
    if (swap.toAmount && getMethod(swap.toNetwork, swap.toToken).ticker === ticker) {
      return {
        id: `swap-${swap.id}`,
        label: 'Convert',
        amountLabel: `+${formatAssetAmount(swap.toAmount, ticker)} ${ticker}`,
        direction: 'in',
        timestamp
      }
    }
    return null
  }
  case TransactionKind.Payout: {
    const payout = transaction.payload as Payout
    const timestamp = new Date(payout.createdAt).getTime() || fallbackTime
    const label = payout.entryCount > 1 ? `Payout · ${payout.entryCount} recipients` : 'Payout'

    // Fiat payouts show on the currency page they were paid in, and debit
    // the funding asset (USDC unless stated otherwise) on that asset's page.
    if ((payout.fiatEntries?.length ?? 0) > 0) {
      const paidInTicker = (payout.fiatEntries ?? [])
        .filter(entry => (entry.currency || 'EUR').toUpperCase() === ticker.toUpperCase())
      if (paidInTicker.length > 0) {
        const paidAmount = paidInTicker.reduce((sum, entry) => sum + Number(entry.amount), 0)
        const beneficiary = paidInTicker.length === 1 ? paidInTicker[0].beneficiaryName?.trim() : undefined
        return {
          id: `payout-${payout.id}`,
          label: beneficiary ? `Payout · ${beneficiary}` : label,
          amountLabel: `-${paidAmount.toFixed(2)} ${ticker.toUpperCase()}`,
          direction: 'out',
          timestamp
        }
      }
      const fundingTicker = payout.fundingCurrency ?? 'USDC'
      if (fundingTicker !== ticker) {
        return null
      }
      const fundedAmount = Number(payout.totalAmountUsd || payout.totalPayoutUsd)
      return {
        id: `payout-${payout.id}`,
        label,
        amountLabel: `-${formatAssetAmount(fundedAmount, ticker)} ${ticker}`,
        direction: 'out',
        timestamp
      }
    }

    const directEntries = (payout.cryptoEntries ?? [])
      .filter(entry => !entry.fundingCurrency && getMethod(entry.network, entry.token).ticker === ticker)
    if (directEntries.length > 0) {
      const totalAmount = directEntries.reduce((sum, entry) => sum + Number(entry.amount), 0)
      return {
        id: `payout-${payout.id}`,
        label,
        amountLabel: `-${formatAssetAmount(totalAmount, ticker)} ${ticker}`,
        direction: 'out',
        timestamp
      }
    }

    // Funded crypto payouts also debit the funding asset, not the destination.
    const fundingTicker = payout.fundingCurrency ?? payout.cryptoEntries?.[0]?.fundingCurrency
    if (fundingTicker === ticker) {
      const fundedAmount = Number(payout.totalAmountUsd || payout.totalPayoutUsd)
      return {
        id: `payout-${payout.id}`,
        label,
        amountLabel: `-${formatAssetAmount(fundedAmount, ticker)} ${ticker}`,
        direction: 'out',
        timestamp
      }
    }
    return null
  }
  case TransactionKind.Deposit: {
    const deposit = transaction.payload as Deposit
    const timestamp = new Date(deposit.depositedAt).getTime() || fallbackTime
    if (deposit.depositMethod.Crypto) {
      if (getMethod(deposit.depositMethod.Crypto.network, deposit.depositMethod.Crypto.token).ticker !== ticker) {
        return null
      }
      return {
        id: `deposit-${deposit.id}`,
        label: 'Deposit',
        amountLabel: `+${formatAssetAmount(deposit.grossAmount, ticker)} ${ticker}`,
        direction: 'in',
        timestamp
      }
    }
    // Bank deposits arrive in the on-ramp asset; net is what hits the balance.
    const fiatMethod = deposit.depositMethod.Fiat
    if (fiatMethod && getMethod(fiatMethod.onrampNetwork || '', fiatMethod.onrampToken).ticker === ticker) {
      return {
        id: `deposit-${deposit.id}`,
        label: 'Bank deposit',
        amountLabel: `+${formatAssetAmount(deposit.netAmount, ticker)} ${ticker}`,
        direction: 'in',
        timestamp
      }
    }
    return null
  }
  }
  return null
}

const ArrowGlyph = ({ direction }: { direction: FeedDirection }): ReactElement => (
  <svg
    viewBox='0 0 16 16'
    width='13'
    height='13'
    fill='none'
    stroke='currentColor'
    strokeWidth='1.8'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'>
    {direction === 'in'
      ? <path d='M8 3.2v9.2m0 0 3.6-3.6M8 12.4 4.4 8.8' />
      : <path d='M8 12.8V3.6m0 0 3.6 3.6M8 3.6 4.4 7.2' />}
  </svg>
)

/**
 * Money home: one banking-first screen for everything the merchant holds.
 * Bank accounts and crypto assets read as one account list, with the money
 * actions (send, receive, convert) a single click away.
 */
const MoneyHome = observer((): ReactElement => {
  const navigate = useNavigate()
  const [testMode] = useTestMode()
  const { has, isLoaded: permissionsLoaded } = usePermissions()

  const canReadBalance = has('ReadBalance')
  const canReadDepositAccounts = has('ReadDepositAccount')
  const canReadEvents = has('ReadEvents')
  const canReadWithdrawals = has('ReadWithdrawal')
  const canReadPayouts = has('ReadPayouts')

  const defaultCurrency = User.organization?.defaultCurrency ?? 'USD'

  const [fiatAccounts, setFiatAccounts] = useState<DepositAccount[]>([])

  const [weekEvents, setWeekEvents] = useState<RadomEvent[]>([])
  const [isLoadingEvents, setIsLoadingEvents] = useState(false)
  const [eventsFailed, setEventsFailed] = useState(false)

  const [recentWithdrawals, setRecentWithdrawals] = useState<WithdrawalRequest[]>([])
  const [recentPayouts, setRecentPayouts] = useState<Payout[]>([])
  const [isLoadingMovements, setIsLoadingMovements] = useState(false)
  const [movementsPartiallyFailed, setMovementsPartiallyFailed] = useState(false)

  const loadFiatAccounts = async (): Promise<void> => {
    if (!canReadDepositAccounts) {
      setFiatAccounts([])
      return
    }
    try {
      const response = await Radom.listDepositAccounts(testMode, 0, 100, undefined, [DepositAccountType.Fiat])
      setFiatAccounts(response.data)
    } catch (err) {
      console.error('Failed to load fiat deposit accounts', err)
      setFiatAccounts([])
    }
  }

  const loadWeekEvents = async (): Promise<void> => {
    if (!canReadEvents) {
      setWeekEvents([])
      return
    }
    setIsLoadingEvents(true)
    setEventsFailed(false)
    try {
      const response = await Radom.listManagedEvents(
        testMode ? testNetworks : prodNetworks,
        startOfDay(subDays(new Date(), 7)).toISOString(),
        new Date().toISOString()
      )
      setWeekEvents(response.data
        .map(parseManagedEvent)
        .filter((event): event is RadomEvent => !!event))
    } catch (err) {
      console.error('Failed to load recent payment events', err)
      setWeekEvents([])
      setEventsFailed(true)
    } finally {
      setIsLoadingEvents(false)
    }
  }

  const loadMovements = async (): Promise<void> => {
    setIsLoadingMovements(true)
    setMovementsPartiallyFailed(false)
    const [withdrawalsResult, payoutsResult] = await Promise.allSettled([
      canReadWithdrawals
        ? Radom.listWithdrawals(0, 14, testMode ? testNetworks : prodNetworks)
        : Promise.resolve({ data: [] as WithdrawalRequest[], total: 0 }),
      canReadPayouts
        ? Radom.listPayouts(0, 14, testMode, [])
        : Promise.resolve({ data: [] as Payout[], total: 0 })
    ])

    setRecentWithdrawals(withdrawalsResult.status === 'fulfilled' ? withdrawalsResult.value.data : [])
    setRecentPayouts(payoutsResult.status === 'fulfilled' ? payoutsResult.value.data : [])
    if (withdrawalsResult.status === 'rejected') {
      console.error('Failed to load recent withdrawals', withdrawalsResult.reason)
    }
    if (payoutsResult.status === 'rejected') {
      console.error('Failed to load recent payouts', payoutsResult.reason)
    }
    if (withdrawalsResult.status === 'rejected' || payoutsResult.status === 'rejected') {
      setMovementsPartiallyFailed(true)
    }
    setIsLoadingMovements(false)
  }

  useEffect(() => {
    if (!permissionsLoaded) {
      return
    }
    if (canReadBalance) {
      void Treasury.loadBalances()
    }
    void loadFiatAccounts()
    void loadWeekEvents()
    void loadMovements()
  }, [permissionsLoaded, testMode])

  // Balance rows are computed exactly like Funds/Overview does for its
  // "Available balance": Radom-treasury managed balances only, valued through
  // Treasury.priceQuotes into the org default currency. Private treasury
  // balances live in their own scope on that page and are not folded in here.
  const balanceRows: BalanceRow[] = Treasury.managedBalances
    .map((source): BalanceRow => {
      // Unified-treasury rows carry an asset ticker and no network; identity
      // resolves through the registry by ticker (same as Funds/Overview).
      const unifiedTicker = source.asset
      const method = unifiedTicker
        ? (getMethodByTicker(unifiedTicker) ?? { name: unifiedTicker, ticker: unifiedTicker })
        : getMethod(source.network ?? '', source.token)
      const isFiat = unifiedTicker
        ? FIAT_ASSET_CODES.includes(unifiedTicker.toUpperCase()) || !!method.isFiat
        : source.network === 'Fiat' || !!method.isFiat
      const ticker = unifiedTicker ?? (isFiat ? (source.token ?? method.ticker) : method.ticker)
      const conversion = Treasury.priceQuotes.find(quote => quote.from.toLowerCase() === ticker.toLowerCase())
      const balance = Number(source.balance)
      const fiatValue = isFiat && ticker === defaultCurrency
        ? balance
        : conversion
          ? balance * conversion.value
          : 0

      return {
        key: unifiedTicker ? `asset:${unifiedTicker}` : `${source.network}:${source.token ?? 'native'}`,
        method,
        ticker,
        isFiat,
        // Unified rows aggregate mainnet inventory; they are never testnet.
        isTestnet: unifiedTicker ? false : !!method.isTestnet,
        balance,
        fiatValue,
        hasQuote: !!conversion || (isFiat && ticker === defaultCurrency),
        networkLabel: isFiat ? ticker : method.hostChain?.name ?? method.name
      }
    })
    .filter(row => testMode ? row.isTestnet : !row.isTestnet)

  // The total nets every balance, negatives included (fee debts subtract, as
  // on the old overview); the asset lists below only show positive holdings.
  const totalEstimatedValue = balanceRows.reduce((total, row) => total + row.fiatValue, 0)
  const positiveBalanceRows = balanceRows.filter(row => row.balance > 0)
  const balancesLoading = !permissionsLoaded || (canReadBalance && Treasury.loading)

  const cryptoGroups: AssetGroup[] = (() => {
    const order: string[] = []
    const groups: Record<string, BalanceRow[]> = {}
    for (const row of positiveBalanceRows) {
      if (row.isFiat) {
        continue
      }
      if (!groups[row.ticker]) {
        groups[row.ticker] = []
        order.push(row.ticker)
      }
      groups[row.ticker].push(row)
    }

    return order
      .map((ticker): AssetGroup => {
        const rows = groups[ticker]
        const first = rows[0]
        // A group can start with a wrapped-token row (WETH carries the ETH
        // ticker), so identity comes from the native chain entry when the
        // registry has one.
        const native = getPaymentMethods().find(method =>
          method.ticker === ticker && !method.hostChain && !method.isFiat)
        const identity = native ?? first.method
        return {
          ticker,
          name: identity.currencyName ?? identity.name,
          logo: identity.logo ?? getMethodLogoByTicker(ticker),
          totalBalance: rows.reduce((sum, row) => sum + row.balance, 0),
          totalFiatValue: rows.reduce((sum, row) => sum + row.fiatValue, 0),
          hasQuote: rows.some(row => row.hasQuote),
          rows
        }
      })
      .sort((a, b) => b.totalFiatValue - a.totalFiatValue)
  })()

  // Any fiat balance actually held with Radom renders as its own row; the
  // account rows above it describe rails, not balances.
  const fiatBalanceRows = positiveBalanceRows.filter(row => row.isFiat)

  // The USD pay-in account today is a Bastion virtual account with US
  // account/routing details, mirroring the Virtual accounts view's filter.
  const bastionUsdAccount =
    fiatAccounts.find(account =>
      !!account.bastionId && !!account.accountDetails.Fiat?.accountType.us && account.status === 'active') ??
    fiatAccounts.find(account => !!account.bastionId && !!account.accountDetails.Fiat?.accountType.us)

  /** Home's conversion order: rates snapshotted on the event, then quotes. */
  const toDefaultCurrency = (amount: number, currency?: string, conversionRates?: IConversionRate[]): number => {
    if (!currency || currency.toLowerCase() === defaultCurrency.toLowerCase()) {
      return amount
    }
    const eventRate = conversionRates?.find(rate =>
      rate.from.toLowerCase() === currency.toLowerCase() &&
      rate.to.toLowerCase() === defaultCurrency.toLowerCase())
    if (eventRate) {
      return amount * Number(eventRate.rate)
    }
    const quote = Treasury.priceQuotes.find(priceQuote => priceQuote.from.toLowerCase() === currency.toLowerCase())
    return quote ? amount * quote.value : amount
  }

  const completedPayments = weekEvents.filter(isCompletedPaymentEvent)
  const weekPaymentCount = completedPayments.length
  const weekPaymentVolume = completedPayments.reduce((total, event) => {
    const gross = Number(event.paymentSummary?.grossAmount ?? event.amount ?? 0)
    return total + toDefaultCurrency(gross, event.paymentSummary?.currency, event.conversionRates)
  }, 0)

  // Daily buckets spanning the same window the events fetch covers (start of
  // seven days ago through today), so the sparkline sums to the volume stat.
  // Days without payments plot as zero.
  const dailyVolumePoints: SparklinePoint[] = (() => {
    const dayStarts = Array.from({ length: 8 }, (_, index) => startOfDay(subDays(new Date(), 7 - index)).getTime())
    const totals = new Map<number, number>(dayStarts.map(dayStart => [dayStart, 0]))
    for (const event of completedPayments) {
      const dayStart = startOfDay(event.date).getTime()
      if (!totals.has(dayStart)) {
        continue
      }
      const gross = Number(event.paymentSummary?.grossAmount ?? event.amount ?? 0)
      const value = toDefaultCurrency(gross, event.paymentSummary?.currency, event.conversionRates)
      totals.set(dayStart, (totals.get(dayStart) ?? 0) + value)
    }
    return dayStarts.map(dayStart => ({ x: dayStart, y: totals.get(dayStart) ?? 0 }))
  })()

  const feedRows: FeedRow[] = (() => {
    const paymentRows: FeedRow[] = completedPayments.map(event => {
      const currency = event.paymentSummary?.currency ?? event.paymentMethod?.ticker ?? ''
      const gross = Number(event.paymentSummary?.grossAmount ?? event.amount ?? 0)
      return {
        id: `payment-${event.id}`,
        label: isOpenBankingPayment(event) ? 'Open banking payment' : 'Payment',
        amountLabel: `+${formatAssetAmount(gross, currency)} ${currency.toUpperCase()}`.trim(),
        direction: 'in' as const,
        timestamp: event.date.getTime(),
        href: `/transactions?selected=${event.id}`
      }
    })

    const withdrawalRows: FeedRow[] = recentWithdrawals.map(withdrawal => {
      const method = getMethod(withdrawal.network, withdrawal.token)
      return {
        id: `withdrawal-${withdrawal.id}`,
        label: 'Withdrawal',
        amountLabel: `-${formatAssetAmount(withdrawal.withdrawalAmount, method.ticker)} ${method.ticker}`,
        direction: 'out' as const,
        statusLabel: withdrawalStatusLabel(withdrawal.withdrawalStatus),
        tone: withdrawalStatusTone(withdrawal.withdrawalStatus),
        timestamp: new Date(withdrawal.requestedAt).getTime(),
        href: `/transactions?selected=${withdrawal.id}`
      }
    })

    const payoutRows: FeedRow[] = recentPayouts.map(payout => {
      // Single-entry payouts read in what the recipient actually gets; only
      // multi-entry batches fall back to the USD total.
      let amountLabel = `-${formatAssetAmount(payout.totalAmountUsd, 'USD')} USD`
      if (payout.entryCount === 1) {
        const fiat = payout.fiatEntries?.[0]
        const crypto = payout.cryptoEntries?.[0]
        if (fiat) {
          amountLabel = `-${formatAssetAmount(fiat.amount, fiat.currency)} ${fiat.currency?.toUpperCase() ?? ''}`.trim()
        } else if (crypto) {
          const method = getMethod(crypto.network, crypto.token)
          amountLabel = `-${formatAssetAmount(crypto.amount, method.ticker)} ${method.ticker}`
        }
      }
      return {
        id: `payout-${payout.id}`,
        label: payout.entryCount > 1 ? `Payout · ${payout.entryCount} recipients` : 'Payout',
        amountLabel,
        direction: 'out' as const,
        statusLabel: payoutStatusLabel(payout.status),
        tone: payoutStatusTone(payout.status),
        timestamp: new Date(payout.createdAt).getTime(),
        href: `/transactions?selected=${payout.id}`
      }
    })

    return [...paymentRows, ...withdrawalRows, ...payoutRows]
      .sort((a, b) => b.timestamp - a.timestamp)
      .slice(0, 14)
  })()

  // The home is the entry surface, so it owns the two setup nudges the old
  // Overview page carried: KYB verification and the hosted-page seller profile.
  const [sellerProfileResolved, setSellerProfileResolved] = useState(!!Radom.sellerProfile)
  useEffect(() => {
    Radom.getSellerProfile().finally(() => setSellerProfileResolved(true))
  }, [])
  const sellerProfile = Radom.sellerProfile
  const sellerProfileIncomplete = sellerProfileResolved &&
    !(sellerProfile?.name && sellerProfile?.logoUrl && sellerProfile?.website)

  const activityLoading = !permissionsLoaded || isLoadingMovements || isLoadingEvents
  const pulseLoading = !permissionsLoaded || isLoadingEvents
  const pulseUnavailable = !canReadEvents || eventsFailed

  interface CashCurrency {
    code: string
    name: string
    symbol: string
    sub: string
    pill?: { tone: 'green' | 'amber' | 'neutral', label: string }
  }

  // The cash section reads as one row per currency; capability and rollout
  // state live on each currency's own page.
  const cashCurrencies: CashCurrency[] = [
    {
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      sub: 'Bank transfers in and out, settled through USDC'
    },
    {
      code: 'USD',
      name: 'US dollar',
      symbol: '$',
      sub: canReadDepositAccounts && bastionUsdAccount
        ? `${bastionUsdAccount.accountDetails.Fiat?.bankName ?? 'Virtual account'} · pay-ins arrive as USDC`
        : 'Virtual accounts for US organizations',
      pill: canReadDepositAccounts && bastionUsdAccount
        ? { tone: 'neutral', label: 'Pay-in only' }
        : undefined
    },
    {
      code: 'GBP',
      name: 'British pound',
      symbol: '£',
      sub: 'GBP accounts and payouts',
      pill: { tone: 'neutral', label: 'Not available in your region' }
    }
  ]

  const renderCurrencyRow = (currency: CashCurrency): ReactElement => {
    const balanceRow = fiatBalanceRows.find(row => row.ticker.toUpperCase() === currency.code)
    const balance = balanceRow?.balance ?? 0

    return <AccountRowButton
      key={currency.code}
      type='button'
      onClick={() => navigate(`/money/${currency.code}`)}>
      <RowIconCircle><CurrencySymbolGlyph>{currency.symbol}</CurrencySymbolGlyph></RowIconCircle>
      <RowMain>
        <RowTitleLine>
          <RowTitle>{currency.code}</RowTitle>
          <RowSub style={{ overflow: 'visible' }}>{currency.name}</RowSub>
          {currency.pill && <InlinePill $tone={currency.pill.tone}>{currency.pill.label}</InlinePill>}
        </RowTitleLine>
        <RowSub>{currency.sub}</RowSub>
      </RowMain>
      <RowEndInline>
        <RowEnd>
          {balanceRow && balance > 0 && <>
            <RowAmount>{formatMoney(balance, currency.code)}</RowAmount>
            {currency.code !== defaultCurrency && balanceRow.hasQuote &&
              <RowAmountSub>≈ {formatMoney(balanceRow.fiatValue, defaultCurrency)}</RowAmountSub>}
          </>}
        </RowEnd>
        <RowChevron aria-hidden='true' style={{ transform: 'rotate(-90deg)' }}>
          <Chevron style={{ fill: 'var(--text-faint, #9aa3b2)', width: 10 }} />
        </RowChevron>
      </RowEndInline>
    </AccountRowButton>
  }

  const renderBankGroup = (): ReactElement => (
    <GroupStack>
      <GroupLabel>Cash</GroupLabel>
      <GroupSurface>
        {cashCurrencies.map(renderCurrencyRow)}
      </GroupSurface>
    </GroupStack>
  )

  // Rows navigate to the asset's own page — the home stays a summary.
  // Balances are network agnostic: chains only surface when moving funds.
  const renderAssetGroup = (group: AssetGroup): ReactElement => {
    return <AccountRowButton
      key={group.ticker}
      type='button'
      onClick={() => navigate(`/money/${encodeURIComponent(group.ticker)}`)}>
      <AssetLogoCircle $logo={group.logo} aria-hidden='true' />
      <RowMain>
        <RowTitleLine>
          <RowTitle>{group.ticker}</RowTitle>
          {group.name !== group.ticker && <RowSub>{group.name}</RowSub>}
        </RowTitleLine>
      </RowMain>
      <RowEndInline>
        <RowEnd>
          <RowAmount>{formatAssetAmount(group.totalBalance, group.ticker)} {group.ticker}</RowAmount>
          <RowAmountSub>
            {group.hasQuote ? `≈ ${formatMoney(group.totalFiatValue, defaultCurrency)}` : 'Value unavailable'}
          </RowAmountSub>
        </RowEnd>
        <RowChevron aria-hidden='true' style={{ transform: 'rotate(-90deg)' }}>
          <Chevron style={{ fill: 'var(--text-faint, #9aa3b2)', width: 10 }} />
        </RowChevron>
      </RowEndInline>
    </AccountRowButton>
  }

  const renderCryptoRows = (): ReactElement => {
    if (balancesLoading) {
      return <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 4 }}>
        <LoadingBar style={{ borderRadius: 10, height: 52, width: '100%' }} />
        <LoadingBar style={{ borderRadius: 10, height: 52, width: '100%' }} />
      </div>
    }

    if (!canReadBalance) {
      return <MutedNote>Your role does not have permission to view crypto balances.</MutedNote>
    }

    if (cryptoGroups.length === 0) {
      return <AccountRowButton type='button' onClick={() => navigate('/receive')}>
        <RowIconCircle><ArrowGlyph direction='in' /></RowIconCircle>
        <RowMain>
          <RowTitle>No crypto balances yet</RowTitle>
          <RowSub>Make your first deposit to get started</RowSub>
        </RowMain>
        <InlineTextButton as='span'>Deposit</InlineTextButton>
      </AccountRowButton>
    }

    return <>{cryptoGroups.map(renderAssetGroup)}</>
  }

  /**
   * The reporting currency, changeable in place. Writes the same org setting
   * as Settings -> Currencies, so the whole dashboard follows.
   */
  const renderCurrencySwitcher = (): ReactElement => (
    <Dropdown
      preferredAlignment='left'
      noChevron
      containerStyle={{ display: 'inline-flex', verticalAlign: 'baseline' }}
      outerStyle={{
        background: 'transparent',
        border: 0,
        display: 'inline-flex',
        minHeight: 0,
        padding: 0
      }}
      innerStyle={{ maxHeight: 280, overflow: 'auto', width: 180 }}
      selectedContent={
        <CurrencySwitchTrigger>
          {defaultCurrency}
          <Chevron style={{ fill: 'currentColor', width: 8 }} />
        </CurrencySwitchTrigger>
      }
      dropdownContent={
        <DropdownContext.Consumer>
          {({ closeDropdown }) => <div>
            {Currencies.map(option => <DropdownItem
              key={option.ticker}
              style={{ alignItems: 'center', display: 'flex', fontSize: 13.5, gap: 8, padding: '9px 12px' }}
              onClick={() => {
                closeDropdown()
                void changeReportingCurrency(option.ticker)
              }}>
              <span style={{ width: 16 }}>{option.icon}</span>
              <span style={{ fontWeight: option.ticker === defaultCurrency ? 550 : 400 }}>{option.ticker}</span>
              {option.ticker === defaultCurrency &&
                <span aria-hidden='true' style={{ color: 'var(--text-faint, #8a919e)', marginLeft: 'auto' }}>✓</span>}
            </DropdownItem>)}
          </div>}
        </DropdownContext.Consumer>
      } />
  )

  const changeReportingCurrency = async (ticker: string): Promise<void> => {
    if (ticker === defaultCurrency) return
    try {
      await Radom.updateSellerProfile({ defaultCurrency: ticker })
      await User.getOrganization(true)
      Treasury.loadPriceQuotes(true)
      successToast(`Reporting currency set to ${ticker}`)
    } catch (err) {
      errorToast(err.response?.data?.error || err.message)
    }
  }

  const renderPulseCard = (): ReactElement => (
    <GroupStack>
      <GroupLabel>Last 7 days</GroupLabel>
      <Surface $compact>
        {pulseLoading
          ? <>
            <LoadingBar style={{ borderRadius: 10, height: 64, width: '100%' }} />
            <LoadingBar style={{ borderRadius: 10, height: 48, width: '100%' }} />
          </>
          : <>
            {!pulseUnavailable && <MoneySparkline
              points={dailyVolumePoints}
              height={64}
              ariaLabel={`Daily payment volume over the last 7 days, estimated in ${defaultCurrency}`}
            />}
            <PulseGrid>
              <PulseStat>
                <PulseLabel>Payment volume</PulseLabel>
                <PulseValue>{pulseUnavailable ? '—' : formatMoney(weekPaymentVolume, defaultCurrency)}</PulseValue>
              </PulseStat>
              <PulseStat>
                <PulseLabel>Payments</PulseLabel>
                <PulseValue>{pulseUnavailable ? '—' : weekPaymentCount}</PulseValue>
              </PulseStat>
            </PulseGrid>
          </>}
        {!pulseLoading && eventsFailed &&
        <MutedNote style={{ padding: 0 }}>Payment activity could not be loaded.</MutedNote>}
        {!pulseLoading && !canReadEvents &&
        <MutedNote style={{ padding: 0 }}>Your role does not have permission to view payment events.</MutedNote>}
      </Surface>
    </GroupStack>
  )

  const renderActivityCard = (): ReactElement => (
    <GroupStack>
      <GroupLabel>Recent activity</GroupLabel>
      <Surface $compact>
        {activityLoading
          ? <LoadingBar style={{ borderRadius: 25, height: 68, width: '100%' }} />
          : feedRows.length === 0
            ? <EmptyState style={{ minHeight: 96 }}>
            No money movement yet. Payments, withdrawals and payouts will appear here.
            </EmptyState>
            : <FeedList>
              {feedRows.map(row => <FeedLink key={row.id} to={row.href}>
                <FeedBadge $direction={row.direction} aria-hidden='true'>
                  <ArrowGlyph direction={row.direction} />
                </FeedBadge>
                <RowMain style={{ gap: 2 }}>
                  <FeedTitle>{row.label}</FeedTitle>
                  <FeedSub>{formatDistance(new Date(row.timestamp), new Date(), { addSuffix: true })}</FeedSub>
                </RowMain>
                <RowEnd style={{ gap: 4 }}>
                  <FeedAmount $direction={row.direction}>{row.amountLabel}</FeedAmount>
                  {row.statusLabel && <InlinePill $tone={row.tone ?? 'neutral'}>{row.statusLabel}</InlinePill>}
                </RowEnd>
              </FeedLink>)}
            </FeedList>}
        {!activityLoading && movementsPartiallyFailed &&
        <MutedNote style={{ padding: 0 }}>Some activity could not be loaded.</MutedNote>}
        <InlineNotice style={{ fontSize: 12 }}>
          <InlineTextLink to='/transactions'>View all</InlineTextLink>
        </InlineNotice>
      </Surface>
    </GroupStack>
  )

  return <Container style={{ flexDirection: 'column' }}>
    <MoneyWorkspace>
      <VerificationBanner />
      {sellerProfileIncomplete &&
        <InlineNotice style={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          justifyContent: 'space-between'
        }}>
          <span>
            Add your seller name, website and logo so contacts recognise your payment pages.
          </span>
          <SecondaryButton
            type='button'
            onClick={() => navigate('/settings')}
            style={{ fontSize: 12.5, minHeight: 32, padding: '0 12px' }}>
            Complete profile
          </SecondaryButton>
        </InlineNotice>}
      <HeroRow>
        <HeroBlock>
          <EyebrowRow>
            <Eyebrow>Total balance</Eyebrow>
            <InlinePill $tone={testMode ? 'amber' : 'green'}>{testMode ? 'Test mode' : 'Live mode'}</InlinePill>
          </EyebrowRow>
          {balancesLoading
            ? <LoadingBar style={{ borderRadius: 10, height: 44, width: 220 }} />
            : <HeroTotal>{canReadBalance ? formatMoney(totalEstimatedValue, defaultCurrency) : '—'}</HeroTotal>}
          <HeroMeta>
            {!balancesLoading && !canReadBalance
              ? 'Your role does not have permission to view balances.'
              : <>
                Estimated value of your bank and crypto balances in{' '}
                {renderCurrencySwitcher()}
              </>}
          </HeroMeta>
          <HeroActions style={{ paddingTop: 6 }}>
            <PrimaryButton onClick={() => navigate('/send')} style={{ minHeight: 40, minWidth: 96 }}>
              Send
            </PrimaryButton>
            <SecondaryButton onClick={() => navigate('/receive')} style={{ minHeight: 40 }}>
              Add funds
            </SecondaryButton>
            <SecondaryButton onClick={() => ConvertController.open()} style={{ minHeight: 40 }}>
              Convert
            </SecondaryButton>
          </HeroActions>
        </HeroBlock>
      </HeroRow>

      <MoneyLayout>
        <MoneyColumn>
          {renderBankGroup()}
          <GroupStack>
            <GroupLabel>Crypto</GroupLabel>
            <GroupSurface>
              {renderCryptoRows()}
            </GroupSurface>
          </GroupStack>
          <HistoryTextButton type='button' onClick={() => navigate('/funds')}>
            Balance history <span aria-hidden='true'>↗</span>
          </HistoryTextButton>
        </MoneyColumn>

        <SideColumn>
          {renderPulseCard()}
          {renderActivityCard()}
        </SideColumn>
      </MoneyLayout>
    </MoneyWorkspace>
  </Container>
})

export default MoneyHome
